
// src/components/ReturnsChart.js - גרף עמודות מקצועי להשוואת תשואות - מותאם מובייל
import React, { useState, useEffect, useRef  } from 'react';
import { TrendingUp, BarChart3, Target, Zap, Calendar, Clock, HelpCircle, Share2, Mail, MessageCircle } from 'lucide-react';

import TooltipPortal from './TooltipPortal'; // וודא שהנתיב נכון
import html2canvas from 'html2canvas';
import { getSpecializationConfigs } from '../config/fundConfigurations';
import api from '../services/api';
import { getDisplayName } from '../config/fundNamesConfig';

const ReturnsChart = ({ product, category, className = "", lastUpdated = null, matchingSpecialization = null }) => {
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
const [selectedFunds, setSelectedFunds] = useState([]); 
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('year1');
  const [showTooltip, setShowTooltip] = useState(false);
const tooltipRef = useRef(null);
const chartRef = useRef(null);
const graphOnlyRef = useRef(null);
const [isMobile, setIsMobile] = useState(false);

const [shareMessage, setShareMessage] = useState('');
const [showShareOptions, setShowShareOptions] = useState(false);

  const formatDataDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // שנתיים אחרונות
    const month = String(now.getMonth() + 1).padStart(2, '0'); // חודש נוכחי עם אפס מוביל
    return `${month}/${year}`;
  };
  // טעינת נתוני השוואה
  useEffect(() => {
    if (category && product?.id) {
      loadComparisonData();
    }
  }, [category, product?.id]);
  useEffect(() => {
  const checkMobile = () => {
    const isMobileNow = window.innerWidth < 768;
    setIsMobile(isMobileNow);
  };
  
  checkMobile();
  window.addEventListener('resize', checkMobile);
  
  return () => window.removeEventListener('resize', checkMobile);
}, []);
const selectAllFunds = () => {
  setSelectedFunds(comparisonData.map(fund => fund.id));
};
useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.relative')) {
      setShowShareOptions(false);
    }
  };

  document.addEventListener('click', handleClickOutside);
  return () => document.removeEventListener('click', handleClickOutside);
}, []);
const clearAllFunds = () => {
  setSelectedFunds([]);
};
  // בדיקת תקינות נתונים
  if (!product || !product.returns) {
    return (
      <div className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 ${className}`}>
        <div className="text-center text-gray-500">
          <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">נתוני תשואות לא זמינים</p>
        </div>
      </div>
    );
  }
// 📧 עדכון פונקציות השיתוף להשתמש באותה פונקציה
const handleShare = async () => {
  const shareText = generateInsightsText(); // השתמש באותה פונקציה
  const shareData = {
    title: `השוואת תשואות קרנות גמל - ${timePeriods.find(p => p.key === selectedTimePeriod)?.label}`,
    text: shareText,
  };

  // בדיקה אם יש תמיכה ב-Web Share API (בעיקר במובייל)
  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      setShareMessage('שותף!');
      setTimeout(() => setShareMessage(''), 2000);
    } catch (error) {
      if (error.name !== 'AbortError') {
        // אם השיתוף נכשל, נציג אפשרויות חלופיות
        setShowShareOptions(true);
      }
    }
  } else {
    // אם אין תמיכה ב-Web Share API, נציג אפשרויות שיתוף
    setShowShareOptions(true);
  }
};

const handleWhatsAppShare = () => {
  const shareText = generateInsightsText(); // השתמש באותה פונקציה
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  window.open(whatsappUrl, '_blank');
  setShowShareOptions(false);
  setShareMessage('שותף בוואטסאפ!');
  setTimeout(() => setShareMessage(''), 2000);
};

const handleEmailShare = () => {
  const shareText = generateInsightsText(); // השתמש באותה פונקציה
  const subject = `השוואת תשואות קרנות גמל - ${timePeriods.find(p => p.key === selectedTimePeriod)?.label}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`;
  window.location.href = emailUrl;
  setShowShareOptions(false);
  setShareMessage('נפתח במייל!');
  setTimeout(() => setShareMessage(''), 2000);
};

const handleCopyForShare = async () => {
  const shareText = generateInsightsText(); // השתמש באותה פונקציה
  try {
    await navigator.clipboard.writeText(shareText);
    setShowShareOptions(false);
    setShareMessage('הועתק לשיתוף!');
    setTimeout(() => setShareMessage(''), 2000);
  } catch (error) {
    setShareMessage('שגיאה');
    setTimeout(() => setShareMessage(''), 2000);
  }
};
const copyInsightsToClipboard = async () => {
  try {
    const insightsText = generateInsightsText(); // השתמש באותה פונקציה
    await navigator.clipboard.writeText(insightsText);
    setShareMessage('הועתק!');
    setTimeout(() => setShareMessage(''), 2000);
  } catch (error) {
    console.error('שגיאה בהעתקת התובנות:', error);
    // fallback - ניסיון אחר להעתקה
    const textArea = document.createElement('textarea');
    textArea.value = generateInsightsText();
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    setShareMessage('הועתק!');
    setTimeout(() => setShareMessage(''), 2000);
  }
};
const exportChartAsImage = async () => {
  if (!graphOnlyRef.current) {
    alert('שגיאה בטעינת הגרף');
    return;
  }

  try {
    // שמור רוחב מקורי
    const originalWidth = graphOnlyRef.current.style.width;
    const originalMinWidth = graphOnlyRef.current.style.minWidth;
    const originalMaxWidth = graphOnlyRef.current.style.maxWidth;

    // קבע רוחב קבוע לתמונה (1200px נראה טוב)
    graphOnlyRef.current.style.width = '1200px';
    graphOnlyRef.current.style.minWidth = '1200px';
    graphOnlyRef.current.style.maxWidth = '1200px';

    // מצא את כל האלמנטים עם טקסט חתוך
    const truncatedElements = graphOnlyRef.current.querySelectorAll('.truncate, [class*="truncate"]');
    const originalStyles = [];
    
    // בטל את החיתוך זמנית
    truncatedElements.forEach(el => {
      originalStyles.push({
        element: el,
        overflow: el.style.overflow,
        textOverflow: el.style.textOverflow,
        whiteSpace: el.style.whiteSpace,
        maxWidth: el.style.maxWidth
      });
      
      el.style.overflow = 'visible';
      el.style.textOverflow = 'clip';
      el.style.whiteSpace = 'normal';
      el.style.maxWidth = 'none';
    });
    
    // הסתר כפתורי פעולה וכפתורי בקרה
    const actionButtons = graphOnlyRef.current.querySelectorAll('.action-buttons-container');
    const controlButtons = graphOnlyRef.current.querySelectorAll('.export-control-buttons');
    const shareOptions = graphOnlyRef.current.querySelector('.share-options');

    const hiddenElements = [];
    
    actionButtons.forEach(btn => {
      hiddenElements.push({ element: btn, display: btn.style.display });
      btn.style.display = 'none';
    });
    
    controlButtons.forEach(btn => {
      hiddenElements.push({ element: btn, display: btn.style.display });
      btn.style.display = 'none';
    });
    
    if (shareOptions) {
      hiddenElements.push({ element: shareOptions, display: shareOptions.style.display });
      shareOptions.style.display = 'none';
    }

    // המתן לרינדור מחדש
    await new Promise(resolve => setTimeout(resolve, 300));

    // צלם
    const canvas = await html2canvas(graphOnlyRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 1200,
      windowWidth: 1200
    });

    // החזר רוחב מקורי
    graphOnlyRef.current.style.width = originalWidth;
    graphOnlyRef.current.style.minWidth = originalMinWidth;
    graphOnlyRef.current.style.maxWidth = originalMaxWidth;

    // החזר את כל הסגנונות המקוריים
    originalStyles.forEach(({ element, overflow, textOverflow, whiteSpace, maxWidth }) => {
      element.style.overflow = overflow;
      element.style.textOverflow = textOverflow;
      element.style.whiteSpace = whiteSpace;
      element.style.maxWidth = maxWidth;
    });
    
    hiddenElements.forEach(({ element, display }) => {
      element.style.display = display;
    });

    // הורד
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `גרף_תשואות_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
      URL.revokeObjectURL(url);
      
      setShareMessage('הורדה הושלמה!');
      setTimeout(() => setShareMessage(''), 2000);
    });

  } catch (error) {
    console.error('שגיאה:', error);
    alert('אירעה שגיאה בהורדה');
  }
};
const TooltipContent = ({ isMobile = false, onClose }) => (
  <div className={`relative ${isMobile ? 'bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200' : ''}`}>
    {/* כפתור סגירה - רק למובייל */}
    {onClose && isMobile && (
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-gray-500 hover:text-red-500 transition-colors text-lg font-bold leading-none w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50"
        aria-label="סגור"
      >
        ×
      </button>
    )}
    
    <h4 className={`font-semibold mb-3 pr-8 ${isMobile ? 'text-green-800' : 'text-white'}`}>
      📊 מה זה מערכת ההשוואות האינטראקטיבית?
    </h4>
    
    <div className={`space-y-3 ${isMobile ? 'text-gray-700' : 'text-gray-300'}`}>
      <p className="text-sm">
        מערכת ההשוואות האינטראקטיבית של גמלנט AI מאפשרת לקבל תמונת עומק מלאה של כלל הקופות ברמת הסיכון שבחרת.
      </p>
      
      <p className="text-sm">
        בהשוואה רב-תקופתית בין הקרנות המובילות בכל קטגוריה – בתשואה מצטברת מ-12 חודשים ועד 5 שנים.
      </p>
      
      <p className="text-sm">
        כך ניתן להבין מהי עקביות הביצועים של כל קופה במסלול המקביל ברמת הסיכון, ולנתח את התנהגות השוק מזווית רחבה ומדויקת.
      </p>
      
    
    </div>
  </div>
);
const generateInsightsText = () => {
  if (chartData.length === 0) return '';
  
  // שימוש ב-chartData כמו שהוא מוצג בגרף (כבר ממוין)
  const bestFund = chartData[0];
  const worstFund = chartData[chartData.length - 1];
  const averageReturn = chartData.reduce((sum, fund) => sum + fund.returnValue, 0) / chartData.length;
  const gap = bestFund.returnValue - worstFund.returnValue;
  const gapOnMillion = (gap / 100) * 1000000;
  
  // מציאת תקופה באותיות
const periodMap = {
  'month1': 'החודש האחרון',
  'ytd': 'מתחילת השנה הנוכחית', 
  'year1': '12 החודשים האחרונים',
  'year2': '24 החודשים האחרונים', 
  'year3': '36 החודשים האחרונים',
  'year4': '48 החודשים האחרונים',
  'year5': '60 החודשים האחרונים'
};
  
  const periodText = periodMap[selectedTimePeriod] || timePeriods.find(p => p.key === selectedTimePeriod)?.label;
  const currentDate = new Date().toLocaleDateString('he-IL');
  
  // שימוש ב-chartData עבור שמות הקרנות (באותו סדר של הגרף)
  const fundNames = chartData.map(fund => fund.fullName);
  const fundNamesText = fundNames.length > 2 
    ? fundNames.slice(0, -1).join(', ') + ' ו' + fundNames.slice(-1)
    : fundNames.join(' ו');

  let insightsText = `📊 מערכת ההשוואות האינטראקטיבית של גמל AI\n`;
  insightsText += `🔍 תובנות מהגרף\n\n`;

  // *** התוכן הזהה למוצג באתר ***
  insightsText += `🔄 השוואה רב תקופתית בין ${fundNamesText}\n\n`;
  insightsText += `📊 השוואה של ${chartData.length} קרנות ל${timePeriods.find(p => p.key === selectedTimePeriod)?.label}\n\n`;
  insightsText += `🥇 הקרן העליונה היא הטובה ביותר\n\n`;
  insightsText += `🔵 הקרן עם 👈 היא הקרן שלך\n\n`;
  insightsText += `---\n\n`;

  // ניתוח מפורט - זהה למוצג באתר
  insightsText += `בתקופת ההשוואה של תשואה מצטברת ב-${periodText} \n\n`;
  
  insightsText += `🥇 במקום הראשון הקרן ${bestFund.fullName} הציגה תשואות של ${bestFund.returnValue > 0 ? '+' : ''}${bestFund.returnValue.toFixed(2)}%\n\n`;
  
  if (chartData.length > 1) {
    insightsText += `🔴 במקום האחרון הקרן ${worstFund.fullName} הציגה תשואות של ${worstFund.returnValue > 0 ? '+' : ''}${worstFund.returnValue.toFixed(2)}%\n\n`;
  }
  
  insightsText += `ממוצע כלל הקרנות שנבדקו הוא ${averageReturn > 0 ? '+' : ''}${averageReturn.toFixed(2)}%\n\n`;
  
  if (chartData.length > 1) {
    insightsText += `פער תשואה בין המקום הראשון לאחרון בטבלה הינו ${gap.toFixed(2)}%\n\n`;
  }
  
  insightsText += `---\n\n`;
  insightsText += `**מה בעצם אומרים כל המספרים הללו על החיסכון שלנו?**\n\n`;
  
  if (chartData.length > 1) {
    insightsText += `על כל מיליון שקלים שהושקעו בתקופה של ${periodText}, פער תשואה בין המקום הראשון לאחרון בטבלה של ${gap.toFixed(2)}%\n\n`;
    insightsText += `משקף פער כספי של **${gapOnMillion.toLocaleString('he-IL')} ₪**\n\n`;
    insightsText += `**ללא התייחסות לפערי דמי הניהול בין הגופים**\n\n`;
    insightsText += `---\n\n`;
  }

  // רשימת כל הקרנות שנבדקו - באותו סדר של הגרף
  insightsText += `📋 רשימת הקרנות שנבדקו (לפי סדר ביצועים):\n\n`;
  chartData.forEach((fund, index) => {
    const rank = index + 1;
    const emoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `${rank}.`;
    const currentMark = fund.isCurrent ? ' 👈 (הקרן שלי)' : '';
    insightsText += `${emoji} ${fund.fullName}${currentMark}\n`;
    insightsText += `   תשואה: ${fund.returnValue > 0 ? '+' : ''}${fund.returnValue.toFixed(2)}%\n\n`;
  });
  
  insightsText += `---\n\n`;

  // גילוי נאות - זהה למוצג באתר
  insightsText += `**גילוי נאות:**\n\n`;

  // אזהרה לתקופה קצרה מ-12 חודשים
  if (selectedTimePeriod === 'month1' || selectedTimePeriod === 'ytd') {
    insightsText += `⚠️ **אזהרה חשובה:**\n`;
    insightsText += `נבחרה תקופת זמן של פחות מ-12 חודשים. על פי רגולציית משרד האוצר - אין להציג השוואות לתקופה קצרה מ-12 חודשים למשקיע הפרטי בהנמקה ביצועים חוקית!\n`;
    insightsText += `לכן השוואה זו אינה ברת קיימא\n\n`;
  }

  if (chartData.length > 1) {
    insightsText += `הקרן ב${bestFund.fullName} אומנם מציגה ביצועים נאים ביחס למתחרים בתקופת הזמן שנמדדה.\n\n`;
  }
  
  insightsText += `עם זאת, הנתונים לבדם אינם מהווים המלצה לביצוע פעולה, ויש לבחון אותם כחלק מתמונה רחבה הכוללת שיקולים אישיים למשקיע/ה – כגון התאמת רמת הסיכון, מדיניות ההשקעה, דמי הניהול, ומשקלה היחסי של הקרן מסך הנכסים הפיננסיים והפנסיוניים שלך.\n\n`;
  
  insightsText += `**🐂 הטיפ של השור מגמל AI:**\n\n`;
  insightsText += `עם כמה שבינה מלאכותית זה פלא הבריאה 🌎\n\n`;
  insightsText += `עדיין, לפני קבלת החלטה אמיתית אשר יכולה להשפיע על יתרות החיסכון שלך – עדיף להתייעץ עם בעל רישיון מוסמך 🧑‍🎓 ואנושי 🧬 הפועל תחת רגולציה של משרד האוצר 🛡️\n\n`;
  
  insightsText += `תאריך נכונות הנתונים: ${lastUpdated || formatDataDate()}\n\n`;
  

  // 🆕 הוסף את זה:
insightsText += `⚠️ **הבהרה משפטית:**\n`;
insightsText += `📊 המידע באתר גמל נט AI נועד להנגיש תובנות והשוואות, אך אינו מהווה ייעוץ פנסיוני, פיננסי או השקעות.\n`;
insightsText += `📄✅ כל החלטה צריכה להיעשות תוך התאמה אישית ובליווי בעל רישיון מוסמך בלבד.\n`;
insightsText += `⏳ תשואות עבר אינן מעידות בהכרח על ביצועים עתידיים, ואין לראות בנתונים באתר התחייבות או המלצה.\n`;
insightsText += `🧠 אנו עושים מאמץ כנה בשילוב מערכות בינה מלאכותית מתקדמות לספק מידע מדויק, אך האחריות לשימוש בו ולפרשנותו היא בידי המשתמש בלבד.\n\n`;

// הוסף קישור לדף המוצר הנוכחי:
insightsText += `🔗 קישור לפרטי הקרן הנוכחית שלך: https://gemelnet.ai/product/${category}/${product.id}\n`;
insightsText += `דיסקליימר מלא: https://gemelnet.ai/legal-disclaimer\n\n`;

  insightsText += `🤖 נוצר באמצעות גמל AI - מערכת ההשוואות החכמה לקרנות גמל\n`;
  insightsText += `📅 ${currentDate}`;
  
  return insightsText;
};


const loadComparisonData = async () => {
  try {
    setLoading(true);
    
    let matchingSpec;
    
    // אם קיבלנו התמחות מ-ProductPage - השתמש בה
    if (matchingSpecialization) {
      matchingSpec = matchingSpecialization;
      // console.log('✅ Using provided matchingSpecialization:', matchingSpec.key);
    } else {
      // אחרת - השתמש בלוגיקה הקיימת
      const specializations = getSpecializationConfigs(category);
      const currentProductId = parseInt(product.id);
      
      matchingSpec = specializations.find(spec => 
        spec.fundIds.includes(currentProductId)
      );
      
      if (!matchingSpec && specializations.length > 0) {
        matchingSpec = specializations[0];
        // console.log('⚠️ Using fallback to first specialization:', matchingSpec.key);
      }
    }
    
    // שאר הקוד ממשיך כרגיל
    if (matchingSpec) {
      let fundIds = [...matchingSpec.fundIds];
      
      // וודא שהקרן הנוכחית ברשימה
      const currentProductId = parseInt(product.id);
      if (!fundIds.includes(currentProductId)) {
        fundIds.unshift(currentProductId);
      }
      
      // הגבל ל-10 קרנות
      const finalFundIds = fundIds.slice(0, 10);
      
      // console.log('📊 Loading comparison data for funds:', finalFundIds);
      
      // טעינת נתוני הקרנות
      const comparisonProducts = await Promise.all(
        finalFundIds.map(async (fundId) => {
          try {
            return await api.getProductById(fundId.toString(), category);
          } catch (error) {
            console.warn(`Failed to load product ${fundId}:`, error);
            return null;
          }
        })
      );
      
      // סינון וארגון הנתונים
      const validProducts = comparisonProducts
        .filter(p => p && p.returns)
        .map((p, index) => ({
          ...p,
          isCurrent: parseInt(p.id) === currentProductId,
          color: getColorForFund(index, parseInt(p.id) === currentProductId),
          shortName: getDisplayName(p).substring(0, 20) + (getDisplayName(p).length > 20 ? '...' : ''),
          fullName: getDisplayName(p)
        }));
        
      // console.log('✅ Loaded valid products:', validProducts.length);
      setComparisonData(validProducts);
    } else {
      console.warn('❌ No matching specialization found');
      setComparisonData([]);
    }
    
  } catch (error) {
    console.error('❌ Error loading comparison data:', error);
    setComparisonData([]);
  } finally {
    setLoading(false);
  }
};

  // צבעים לקרנות
  const getColorForFund = (index, isCurrent) => {
    if (isCurrent) return { 
      primary: '#2563eb', 
      light: '#dbeafe', 
      gradient: 'from-blue-500 to-blue-600' 
    };
    
    const colors = [
      { primary: '#10b981', light: '#d1fae5', gradient: 'from-emerald-500 to-emerald-600' },
      { primary: '#f59e0b', light: '#fef3c7', gradient: 'from-amber-500 to-amber-600' },
      { primary: '#ef4444', light: '#fee2e2', gradient: 'from-red-500 to-red-600' },
      { primary: '#8b5cf6', light: '#ede9fe', gradient: 'from-violet-500 to-violet-600' },
      { primary: '#06b6d4', light: '#cffafe', gradient: 'from-cyan-500 to-cyan-600' },
      { primary: '#ec4899', light: '#fce7f3', gradient: 'from-pink-500 to-pink-600' },
      { primary: '#84cc16', light: '#ecfccb', gradient: 'from-lime-500 to-lime-600' },
      { primary: '#f97316', light: '#fed7aa', gradient: 'from-orange-500 to-orange-600' },
    ];
    
    return colors[index % colors.length];
  };
// צבעים טורקיז לתקופות זמן
const getTealColorForPeriod = (periodKey) => {
  const tealColors = {
    'month1': 'bg-[#009393]',
    'ytd': 'bg-[#008b8b]', 
    'year1': 'bg-[#008383]',
    'year2': 'bg-[#007a7a]',
    'year3': 'bg-[#007272]',
    'year4': 'bg-[#006a6a]',
    'year5': 'bg-[#006a6a]'
  };
  
  return tealColors[periodKey] || 'bg-[#009393]';
};
  // תקופות זמן זמינות
  const timePeriods = [
    { key: 'month1', label: 'תשואה חודשית אחרונה', shortLabel: 'תשואה חודשית אחרונה' },
    { key: 'ytd', label: 'תשואה מצטברת מתחילת שנה', shortLabel: 'תשואה מצטברת מתחילת שנה' },
    { key: 'year1', label: 'תשואה מצטברת 12 חודשים אחרונים', shortLabel: 'תשואה מצטברת 12 חודשים אחרונים' },
    { key: 'year2', label: 'תשואה מצטברת 24 חודשים אחרונים', shortLabel: 'תשואה מצטברת 24 חודשים אחרונים' },
    { key: 'year3', label: 'תשואה מצטברת 36 חודשים אחרונים', shortLabel: 'תשואה מצטברת 36 חודשים אחרונים' },
    { key: 'year4', label: 'תשואה מצטברת 48 חודשים אחרונים', shortLabel: 'תשואה מצטברת 48 חודשים אחרונים' },
    { key: 'year5', label: 'תשואה מצטברת 60 חודשים אחרונים', shortLabel: 'תשואה מצטברת 60 חודשים אחרונים' }
  ];

  // הכנת נתונים לגרף - ממויין לפי תשואה
  const getChartData = () => {
    const selectedFundsData = comparisonData.filter(fund => selectedFunds.includes(fund.id));
    
    const fundsWithData = selectedFundsData
      .filter(fund => fund.returns[selectedTimePeriod] !== null && fund.returns[selectedTimePeriod] !== undefined)
      .map(fund => ({
        ...fund,
        returnValue: fund.returns[selectedTimePeriod]
      }))
      .sort((a, b) => b.returnValue - a.returnValue);

    return fundsWithData;
  };

  const chartData = getChartData();

  // חישוב גבולות הגרף
  const getMaxValue = () => {
    if (chartData.length === 0) return 20;
    const maxReturn = Math.max(...chartData.map(fund => fund.returnValue));
    return Math.max(maxReturn * 1.2, 10);
  };

  const maxValue = getMaxValue();

  // בחירת קרנות
  const toggleFund = (fundId) => {
    setSelectedFunds(prev => 
      prev.includes(fundId) 
        ? prev.filter(id => id !== fundId)
        : [...prev, fundId]
    );
  };

  // בחירת תקופה
  const handleTimePeriodChange = (periodKey) => {
    setSelectedTimePeriod(periodKey);
  };

  // בדיקה אם תקופה זמינה
  const isTimePeriodAvailable = (periodKey) => {
    return comparisonData.some(fund => 
      selectedFunds.includes(fund.id) && 
      fund.returns[periodKey] !== null && 
      fund.returns[periodKey] !== undefined
    );
  };

return (
  <div ref={chartRef} data-chart-ref className={`bg-gradient-to-br from-slate-50 to-blue-50  rounded-xl border border-slate-200 overflow-hidden ${className}`}>
    {/* כותרת */}
<div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white p-3 sm:p-4">
  <h3 className="text-base sm:text-lg font-bold mb-1 flex items-center gap-2">
    <div className="p-1.5 bg-white/20 rounded-lg">
      <BarChart3 className="h-4 w-4" />
    </div>
    מערכת ההשוואות האינטראקטיבית של גמל AI
    
    {/* כפתור הטולטיפ */}
    <button
      ref={tooltipRef}
      onMouseEnter={() => {
        // console.log('Mouse enter - isMobile:', isMobile);
        if (!isMobile) {
          setShowTooltip(true);
        }
      }}
      onMouseLeave={() => {
        // console.log('Mouse leave - isMobile:', isMobile);
        if (!isMobile) {
          setShowTooltip(false);
        }
      }}
      onClick={() => {
        // console.log('Click - isMobile:', isMobile, 'current showTooltip:', showTooltip);
        setShowTooltip(!showTooltip);
      }}
      className="p-1 md:p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
      aria-label="מידע נוסף על מערכת ההשוואות"
    >
      <HelpCircle className="h-4 w-4 md:h-5 md:w-5 text-white hover:text-blue-200" />
    </button>
    
    {lastUpdated && (
      <span className="text-xs sm:text-sm font-normal text-blue-100 mr-auto">
        הנתונים מעודכנים לחודש {lastUpdated}
      </span>
    )}
  </h3>
  
  {/* טולטיפ למובייל - מיד אחרי הכותרת */}
  {isMobile && showTooltip && (
    <div className="mt-3 animate-in slide-in-from-top duration-300">
      <TooltipContent 
        isMobile={true} 
        onClose={() => setShowTooltip(false)} 
      />
    </div>
  )}
  
  <p className="text-blue-100 text-xs sm:text-sm">
    השוואה רב-תקופתית בין {comparisonData.length} הקרנות המובילות בכל קטגוריה, באותה רמת סיכון – עם סינון חכם לכל תקופות ההשוואה, בהצגה ברורה ונוחה למשקיע.
    מעכשיו, בזכות המערכת כל הנתונים, התשואות וההשוואות נגישים לך בלחיצת כפתור.
    בלי טבלאות מסובכות, בלי שעות של חיפושים – פשוט תובנות ברורות שמובילות להחלטות מושכלות יותר.
  </p>
</div>

{/* טולטיפ לדקסטופ - הוסף מיד אחרי הכותרת, לפני div עם class="p-3 sm:p-4" */}
{!isMobile && showTooltip && tooltipRef.current && (
  <TooltipPortal targetRef={tooltipRef}>
    <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-md border border-gray-600 text-sm leading-relaxed">
      <TooltipContent 
        isMobile={false} 
        onClose={() => setShowTooltip(false)} 
      />
      
      {/* חץ של הטולטיפ */}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  </TooltipPortal>
)}

<div className="p-3 sm:p-4">
  {/* כפתורי בחירה ובחירת קרנות - עובר ראשון */}
  {!loading && comparisonData.length > 0 && (
    <div className="mb-3 bg-white rounded-lg border shadow-sm p-2.5">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
          <Target className="h-3 w-3" />
           בחר קרנות ברמת סיכון {product?.riskLevel.level || 'N/A'} מתוך 5
        </h4>
<div className="export-control-buttons flex gap-2">
  <button
    onClick={selectAllFunds}
    className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
  >
    בחר הכל
  </button>
  <button
    onClick={clearAllFunds}
    className="px-2 py-1 text-xs bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
  >
    איפוס
  </button>
</div>
      </div>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-3 sm:gap-2">
        {comparisonData.map((fund) => {
          const isSelected = selectedFunds.includes(fund.id);
          const hasDataForPeriod = fund.returns[selectedTimePeriod] !== null && fund.returns[selectedTimePeriod] !== undefined;
          return (
            <button
              key={fund.id}
              onClick={() => toggleFund(fund.id)}
              className={`flex items-center gap-2 p-2 rounded-md text-xs sm:text-sm border transition-all duration-200 ${
                isSelected
                  ? `text-white border-transparent shadow-sm`
                  : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50'
              }`}
              style={isSelected ? { 
                background: `linear-gradient(135deg, ${fund.color.primary}, ${fund.color.primary}dd)`
              } : {}}
            >
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div 
                  className={`w-3 h-3 rounded-full shadow-sm ${
                    isSelected ? 'border border-white' : 'border border-gray-300'
                  }`}
                  style={{ backgroundColor: fund.color.primary }}
                />
              </div>
              
              <div className="flex-1 min-w-0 text-right">
                <div className="font-medium truncate">
                  {fund.shortName}
                  {fund.isCurrent && <span className={`mr-1 text-xs ${isSelected ? 'text-white' : 'text-blue-600'}`}>👈</span>}
                </div>
              </div>
              
              <div className="text-left flex-shrink-0">
                {hasDataForPeriod ? (
                  <div className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-green-600'}`}>
                    {fund.returns[selectedTimePeriod] > 0 ? '+' : ''}{fund.returns[selectedTimePeriod].toFixed(2)}%
                  </div>
                ) : (
                  <div className={`text-xs ${isSelected ? 'text-white opacity-70' : 'text-gray-400'}`}>
                    אין נתונים
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  )}

{/* בחירת תקופת זמן עם טווחי השקעה */}
{/* בחירת תקופת זמן - עובר שני */}
<div className="mb-3 bg-white rounded-lg border shadow-sm p-2.5">
  <h4 className="text-xs font-bold text-gray-800 mb-4 flex items-center gap-1.5">
    <Calendar className="h-3 w-3 " />
    בחירת תקופת זמן להשוואה רוחבית לנתוני {lastUpdated}: 
  </h4>

  {/* דקסטופ - שורה אחת עם סימוני טווח */}
  <div className="hidden xl:block">
    {/* סימוני טווח זמן - דקסטופ */}
    <div className="mb-6">
      <div className="grid grid-cols-7 gap-2">
        
        {/* טווח קצר - 3 עמודות ראשונות */}
        <div className="col-span-3 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4">
            <div className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-600" />
              <span className="text-sm font-bold text-green-700">תשואות מצטברות בטווח הקצר</span>
            </div>
          </div>
          <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-green-300 to-green-200 rounded-full"></div>
        </div>
        
        {/* טווח בינוני - 3 עמודות באמצע */}
        <div className="col-span-3 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4">
            <div className="inline-flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-bold text-blue-700">תשואות מצטברות בטווח הבינוני</span>
            </div>
          </div>
          <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-blue-300 to-blue-200 rounded-full"></div>
        </div>
        
        {/* טווח ארוך - עמודה אחרונה */}
        <div className="col-span-1 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 whitespace-nowrap">
            <div className="inline-flex items-center gap-1">
              <Target className="h-3 w-3 text-purple-600" />
              <span className="text-xs font-bold text-purple-700">תשואות מצטברות בטווח הארוך</span>
            </div>
          </div>
          <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-purple-300 to-purple-200 rounded-full"></div>
        </div>
        
      </div>
    </div>

    {/* כרטיסיות בשורה אחת - דקסטופ */}
    <div className="grid grid-cols-7 gap-2">
      {timePeriods.map((period) => {
        const isAvailable = isTimePeriodAvailable(period.key);
        const isSelected = selectedTimePeriod === period.key;
        
        return (
          <button
            key={period.key}
            onClick={() => isAvailable && handleTimePeriodChange(period.key)}
            disabled={!isAvailable}
            className={`p-2 rounded-lg text-xs border-2 transition-all duration-300 min-h-[3rem] flex items-center justify-center ${
              isSelected && isAvailable
                ? getTealColorForPeriod(period.key) + ' text-white border-current shadow-lg transform scale-105'
                : isAvailable
                ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
            }`}
          >
            <div className="font-medium text-center">
              <span className="block sm:hidden">{period.shortLabel}</span>
              <span className="hidden sm:block">{period.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  </div>

  {/* מובייל וטאבלט - שלוש שורות */}
  <div className="xl:hidden space-y-4">
    
    {/* טווח קצר */}
    <div>
      <div className="mb-2">
        <div className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3 text-green-600" />
          <span className="text-xs font-bold text-green-700">תשואות מצטברות בטווח הקצר</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {timePeriods.filter(p => ['month1', 'ytd', 'year1'].includes(p.key)).map((period) => {
          const isAvailable = isTimePeriodAvailable(period.key);
          const isSelected = selectedTimePeriod === period.key;
          
          return (
            <button
              key={period.key}
              onClick={() => isAvailable && handleTimePeriodChange(period.key)}
              disabled={!isAvailable}
              className={`p-2 rounded-lg text-xs border-2 transition-all duration-300 min-h-[3rem] flex items-center justify-center ${
                isSelected && isAvailable
                  ? getTealColorForPeriod(period.key) + ' text-white border-current shadow-lg transform scale-105'
                  : isAvailable
                  ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="font-medium text-center">
                <span className="block sm:hidden">{period.shortLabel}</span>
                <span className="hidden sm:block">{period.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>

    {/* טווח בינוני */}
    <div>
      <div className="mb-2">
        <div className="inline-flex items-center gap-1">
          <BarChart3 className="h-3 w-3 text-blue-600" />
          <span className="text-xs font-bold text-blue-700">תשואות מצטברות בטווח הבינוני</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {timePeriods.filter(p => ['year2', 'year3', 'year4'].includes(p.key)).map((period) => {
          const isAvailable = isTimePeriodAvailable(period.key);
          const isSelected = selectedTimePeriod === period.key;
          
          return (
            <button
              key={period.key}
              onClick={() => isAvailable && handleTimePeriodChange(period.key)}
              disabled={!isAvailable}
              className={`p-2 rounded-lg text-xs border-2 transition-all duration-300 min-h-[3rem] flex items-center justify-center ${
                isSelected && isAvailable
                  ? getTealColorForPeriod(period.key) + ' text-white border-current shadow-lg transform scale-105'
                  : isAvailable
                  ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="font-medium text-center">
                <span className="block sm:hidden">{period.shortLabel}</span>
                <span className="hidden sm:block">{period.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>

    {/* טווח ארוך */}
    <div>
      <div className="mb-2">
        <div className="inline-flex items-center gap-1">
          <Target className="h-3 w-3 text-purple-600" />
          <span className="text-xs font-bold text-purple-700"> תשואות מצטברות בטווח הארוך</span>
        </div>
      </div>
      <div className="flex justify-center">
        {timePeriods.filter(p => p.key === 'year5').map((period) => {
          const isAvailable = isTimePeriodAvailable(period.key);
          const isSelected = selectedTimePeriod === period.key;
          
          return (
            <button
              key={period.key}
              onClick={() => isAvailable && handleTimePeriodChange(period.key)}
              disabled={!isAvailable}
              className={`p-2 rounded-lg text-xs border-2 transition-all duration-300 min-h-[3rem] flex items-center justify-center ${
                isSelected && isAvailable
                  ? getTealColorForPeriod(period.key) + ' text-white border-current shadow-lg transform scale-105'
                  : isAvailable
                  ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="font-medium text-center">
                <span className="block sm:hidden">{period.shortLabel}</span>
                <span className="hidden sm:block">{period.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>

  </div>
</div>

<div ref={graphOnlyRef}>
      {/* הגרף - מותאם מובייל עם מסך ריק */}
      <div className="bg-white rounded-lg border shadow-sm p-3">
        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-gray-600 text-sm">טוען נתונים...</p>
          </div>
        ) : selectedFunds.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium mb-2">לא נבחרו קרנות להשוואה</p>
            <p className="text-xs text-gray-400 mb-4">בחר קרנות מהרשימה למעלה כדי להציג את הגרף</p>
            <button
              onClick={selectAllFunds}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
            >
              בחר את כל הקרנות
            </button>
          </div>
        ) : chartData.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <Zap className="h-3 w-3 text-yellow-500" />
               {timePeriods.find(p => p.key === selectedTimePeriod)?.label}
              </h4>
              <div className="text-xs text-gray-500">
                {chartData.length} קרנות
              </div>
            </div>
            
            {/* אזור העמודות - מותאם מובייל */}
            <div className="space-y-2">
              {chartData.map((fund, index) => {
                const percentage = Math.max((fund.returnValue / maxValue) * 100, 3);
                const isPositive = fund.returnValue >= 0;
                
                return (
                  <div key={fund.id} className="relative">
                    {/* מידע הקרן - קומפקטי */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {index === 0 ? (
                            <img src="/golden-bull.png" alt="שור זהב" className="h-6 w-6 sm:h-8 sm:w-8 zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg filter brightness-110 cursor-pointer" />
                          ) : index === chartData.length - 1 && chartData.length > 1 ? (
                            <img src={`/ScaredBear${chartData.length}.png`} alt="דוב מפוחד" className="h-6 w-6 sm:h-8 sm:w-8 zoom-pulse hover:scale-150 transition-transform duration-500 drop-shadow-lg cursor-pointer" />
                          ) : (
                            <span className="text-sm sm:text-base font-bold text-gray-500">#{index + 1}</span>
                          )}
                        </div>
                        
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-gray-800 flex items-center gap-1 truncate">
                            {fund.shortName}
                            {fund.isCurrent && <span className="text-blue-600 text-xs">👈</span>}
                          </div>
                       
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                          {fund.returnValue > 0 ? '+' : ''}{fund.returnValue.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                    
                    {/* העמודה - קומפקטית */}
                    <div className="relative bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${fund.color.gradient} rounded-full transition-all duration-500 ease-out relative overflow-hidden`}
                        style={{ width: `${percentage}%` }}
                      >
                        {/* אפקט ברק */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12" />
                        
                        {fund.isCurrent && (
                          <div className="absolute inset-0 border border-blue-300 rounded-full" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* סטטיסטיקות - קומפקטיות */}
       {/* סטטיסטיקות - קומפקטיות */}
<div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
  <div className="text-center">
    <div className="text-sm font-bold text-green-600">
      +{chartData[0]?.returnValue.toFixed(2)}%
    </div>
    <div className="text-xs text-gray-500">הטובה ביותר</div>
  </div>
  
  <div className="text-center">
    <div className="text-sm font-bold text-blue-600">
      {(() => {
        const average = chartData.reduce((sum, item) => sum + item.returnValue, 0) / chartData.length;
        return `${average > 0 ? '+' : ''}${average.toFixed(2)}%`;
      })()}
    </div>
    <div className="text-xs text-gray-500">ממוצע</div>
  </div>
  
  <div className="text-center">
    <div className="text-sm font-bold text-red-500">
      {chartData[chartData.length - 1]?.returnValue > 0 ? '+' : ''}{chartData[chartData.length - 1]?.returnValue.toFixed(2)}%
    </div>
    <div className="text-xs text-gray-500">הנמוכה ביותר</div>
  </div>
</div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">אין נתונים זמינים לתקופה הנבחרת</p>
          </div>
        )}
      </div>

      {/* סיכום - קומפקטי */}
      {/* הודעה לקרן יחידה */}
{!loading && chartData.length === 1 && (
  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-2">
      <div className="text-amber-600">⚠️</div>
      <h5 className="text-sm font-semibold text-amber-800">לצורך השוואה מועילה</h5>
    </div>
    <p className="text-amber-700 text-xs">
      נבחרה רק קרן אחת. בחר קרנות נוספות מהרשימה למעלה כדי לקבל תובנות השוואתיות משמעותיות.
    </p>
    <button
      onClick={selectAllFunds}
      className="mt-2 px-3 py-1 bg-amber-600 text-white text-xs rounded-md hover:bg-amber-700 transition-colors"
    >
      בחר את כל הקרנות
    </button>
  </div>
)}
{/* סיכום - קומפקטי עם רקע */}
{!loading && chartData.length > 1 && (
  <div
    className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800  rounded-lg overflow-hidden"
   
  >
    <div className="p-4 relative z-10 text-white">
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-bold flex items-center gap-1.5">
          <Target className="h-3 w-3" />
          תובנות מהגרף
        </h5>
<div className="action-buttons-container flex items-center gap-2">
  {shareMessage && (
    <span className="text-xs text-blue-200 font-medium">
      {shareMessage}
    </span>
  )}
  <button
    onClick={exportChartAsImage}
    className="px-2 py-1 text-xs bg-white/20 text-white rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
    title="הורד כתמונה"
  >
    <svg 
      className="h-3 w-3" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
    תמונה
  </button>
  <button
    onClick={() => copyInsightsToClipboard()}
    className="px-2 py-1 text-xs bg-white/20 text-white rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
    title="העתק תובנות"
  >
    <span>📋</span>
    העתק
  </button>
  <div className="relative">
    <button
      onClick={handleShare}
      className="px-2 py-1 text-xs bg-green-600/80 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-1"
      title="שתף תובנות"
    >
      <Share2 className="h-3 w-3" />
      שתף
    </button>
    
    {showShareOptions && (
      <div className="share-options absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 min-w-[150px]">
        <button
          onClick={handleWhatsAppShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-green-50 rounded-md transition-colors"
        >
          <MessageCircle className="h-4 w-4 text-green-600" />
          וואטסאפ
        </button>
        <button
          onClick={handleEmailShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-md transition-colors"
        >
          <Mail className="h-4 w-4 text-blue-600" />
          אימייל
        </button>
        <button
          onClick={handleCopyForShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
        >
          <span className="text-gray-600">📋</span>
          העתק לשיתוף
        </button>
      </div>
    )}
  </div>
</div>

      </div>
      <div className="space-y-2 text-blue-100 text-xs">
        {/* הסבר בסיסי */}
        {(() => {
  const fundNames = chartData.map(fund => fund.fullName);
  const fundNamesText = fundNames.length > 2 
    ? fundNames.slice(0, -1).join(', ') + ' ו' + fundNames.slice(-1)
    : fundNames.join(' ו');
  
  return (
    <p><strong>🔄 השוואה רב תקופתית בין {fundNamesText}</strong></p>
  );
})()}
      <p><strong>📊 השוואה של {chartData.length} קרנות ל{timePeriods.find(p => p.key === selectedTimePeriod)?.label}</strong></p>
        <p><strong>🥇 הקרן העליונה היא הטובה ביותר</strong></p>
        <p><strong>🔵 הקרן עם 👈 היא הקרן שלך</strong></p>
        
        {/* קו הפרדה */}
        <div className="border-t border-blue-400 opacity-50 my-3"></div>
        
        {/* ניתוח מפורט */}
        {(() => {
          const bestFund = chartData[0];
          const worstFund = chartData[chartData.length - 1];
          const averageReturn = chartData.reduce((sum, fund) => sum + fund.returnValue, 0) / chartData.length;
          const gap = bestFund.returnValue - worstFund.returnValue;
          const gapOnMillion = (gap / 100) * 1000000;
          
          // מציאת תקופה באותיות
    const periodMap = {
  'month1': 'החודש האחרון',
  'ytd': 'מתחילת השנה הנוכחית', 
  'year1': '12 החודשים האחרונים',
  'year2': '24 החודשים האחרונים', 
  'year3': '36 החודשים האחרונים',
  'year4': '48 החודשים האחרונים',
  'year5': '60 החודשים האחרונים'
};
          
          const periodText = periodMap[selectedTimePeriod] || timePeriods.find(p => p.key === selectedTimePeriod)?.label;
          
          return (
            <div className="space-y-2">
           <p>בתקופת ההשוואה של תשואה מצטברת ב{periodText}</p>
  <p className="flex items-center gap-1.5">
  <img src="/golden-bull.png" alt="שור זהב" className="h-4 w-4 flex-shrink-0 zoom-pulse" />
  במקום הראשון הקרן {bestFund.fullName} הציגה תשואות של {bestFund.returnValue > 0 ? '+' : ''}{bestFund.returnValue.toFixed(2)}%
</p>
<p className="flex items-center gap-1.5">
  <img src={`/ScaredBear${chartData.length}.png`} alt="דוב מפוחד" className="h-4 w-4 flex-shrink-0 zoom-pulse" />
  במקום האחרון הקרן {worstFund.fullName} הציגה תשואות של {worstFund.returnValue > 0 ? '+' : ''}{worstFund.returnValue.toFixed(2)}%
</p>
              <p>ממוצע כלל הקרנות שנבדקו הוא {averageReturn > 0 ? '+' : ''}{averageReturn.toFixed(2)}%</p>
                 <p> פער תשואה בין המקום הראשון לאחרון בטבלה הינו {gap.toFixed(2)}% </p>
              <div className="border-t border-blue-400 opacity-30 my-2"></div>

            <p className="text-sm font-bold text-yellow-300 mb-3">מה בעצם אומרים כל המספרים הללו על החיסכון שלנו?</p>
          <p>על כל מיליון שקלים שהושקעו ב{periodText}, פער תשואה בין המקום הראשון לאחרון בטבלה של {gap.toFixed(2)}% </p>
              <p>משקף פער כספי של <strong>{gapOnMillion.toLocaleString('he-IL')} ₪</strong></p>
              <p className="text-sm font-bold  mt-2">**ללא התייחסות לפערי דמי הניהול בין הגופים**</p>
              <div className="border-t border-blue-400 opacity-30 my-3"></div>
              
              {/* גילוי נאות */}
{/* גילוי נאות */}
<div className="space-y-2 pt-2">
  {(selectedTimePeriod === 'month1' || selectedTimePeriod === 'ytd') && (
    <p className="text-blue-200 font-bold text-red-500 text-sm mb-2">
       נבחרה תקופת זמן של פחות מ-12 חודשים. על פי רגולציית משרד האוצר - אין להציג השוואות לתקופה קצרה מ-12 חודשים למשקיע הפרטי בהנמקה ביצועים חוקית !
       לכן השוואה זו אינה ברת קיימא
    </p>
  )}
  <p>הקרן ב{bestFund.fullName} אומנם מציגה ביצועים נאים ביחס למתחרים בתקופת הזמן שנמדדה.</p>
  <p>עם זאת, הנתונים לבדם אינם מהווים המלצה לביצוע פעולה, ויש לבחון אותם כחלק מתמונה רחבה הכוללת שיקולים אישיים למשקיע/ה – כגון התאמת רמת הסיכון, מדיניות ההשקעה, דמי הניהול, ומשקלה היחסי של הקרן מסך הנכסים הפיננסיים והפנסיוניים שלך.</p>
  <p className="text-xs text-blue-200">
    תאריך נכונות הנתונים: {lastUpdated || formatDataDate()}
  </p>
  
  <div className="border-t border-blue-400 opacity-30 my-2"></div>
                <p className="font-bold">🐂 הטיפ של השור מגמל AI:</p>
          <p>עם כמה שבינה מלאכותית זה פלא הבריאה 🌎 </p>
                <p>עדיין, לפני קבלת החלטה אמיתית אשר יכולה להשפיע על יתרות החיסכון שלך – עדיף להתייעץ עם בעל רישיון מוסמך 🧑‍🎓 ואנושי 🧬  הפועל תחת רגולציה של משרד האוצר 🛡️</p>
                <div className="border-t border-blue-400 opacity-30 my-3"></div>
  <p className="text-xs text-blue-100 leading-relaxed">
    ⚠️ <strong>דיסקליימר:</strong> המידע המוצג באתר הינו כללי ואינפורמטיבי בלבד. אין לראות בו ייעוץ השקעות, שיווק השקעות, חוות דעת מקצועית או המלצה. השימוש בתוכן האתר נעשה באחריות המשתמש בלבד.{' '}
    <a href="/legal-disclaimer" target="_blank" className="text-blue-100 hover:text-yellow-900 underline">
      ראו דיסקליימר המלא
    </a>
  </p>
  <p>תאריך נכונות הנתונים: {lastUpdated || formatDataDate()}</p>
              </div>
<div className="action-buttons-container flex items-center gap-2">
  {shareMessage && (
    <span className="text-xs text-blue-200 font-medium">
      {shareMessage}
    </span>
  )}
  <button
    onClick={exportChartAsImage}
    className="px-2 py-1 text-xs bg-white/20 text-white rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
    title="הורד כתמונה"
  >
    <svg 
      className="h-3 w-3" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
    תמונה
  </button>
  <button
    onClick={() => copyInsightsToClipboard()}
    className="px-2 py-1 text-xs bg-white/20 text-white rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
    title="העתק תובנות"
  >
    <span>📋</span>
    העתק
  </button>
  <div className="relative">
    <button
      onClick={handleShare}
      className="px-2 py-1 text-xs bg-green-600/80 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-1"
      title="שתף תובנות"
    >
      <Share2 className="h-3 w-3" />
      שתף
    </button>
    
    {showShareOptions && (
      <div className="share-options absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 min-w-[150px]">
        <button
          onClick={handleWhatsAppShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-green-50 rounded-md transition-colors"
        >
          <MessageCircle className="h-4 w-4 text-green-600" />
          וואטסאפ
        </button>
        <button
          onClick={handleEmailShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-md transition-colors"
        >
          <Mail className="h-4 w-4 text-blue-600" />
          אימייל
        </button>
        <button
          onClick={handleCopyForShare}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
        >
          <span className="text-gray-600">📋</span>
          העתק לשיתוף
        </button>
      </div>
    )}
  </div>
</div>
            </div>
          );
        })()}
      </div>
    </div>
  </div>
)}
    </div>
  </div>
    <style>{`
      @keyframes zoomPulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.3);
        }
      }
      
      .zoom-pulse {
        animation: zoomPulse 2s infinite ease-in-out;
      }
    `}</style>
  </div>
);
};

export default ReturnsChart;